import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  IconButton,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { theme } from "../../theme/colors";
import { fetchEvents } from "../../utils/eventApi";

function formatDate(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
}

export default function EventSelector({
  selectedEvent,
  onEventSelect,
  label = "Link to an Event",
  eventTypeFilter,
  includePast = false,
  error,
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page_size: 50,
        collapse_series: true,
        ...(!includePast && { after: new Date().toISOString() }),
        ...(includePast && { include_past: "true" }),
        ...(eventTypeFilter && { event_type: eventTypeFilter }),
      };
      const data = await fetchEvents(params);
      const list = Array.isArray(data) ? data : (data?.results || []);
      setEvents(list);
      setFilteredEvents(list);
    } catch (err) {
      console.error("Error loading events:", err);
      setEvents([]);
      setFilteredEvents([]);
    } finally {
      setLoading(false);
    }
  }, [includePast, eventTypeFilter]);

  useEffect(() => {
    if (open) {
      loadEvents();
    }
  }, [open, loadEvents]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredEvents(events);
    } else {
      const q = searchQuery.toLowerCase();
      setFilteredEvents(
        events.filter(
          (e) =>
            e.title?.toLowerCase().includes(q) ||
            e.city?.toLowerCase().includes(q) ||
            e.organizer_name?.toLowerCase().includes(q)
        )
      );
    }
  }, [searchQuery, events]);

  const handleEventSelect = (event) => {
    onEventSelect(event);
    setOpen(false);
    setSearchQuery("");
  };

  const handleClear = () => {
    onEventSelect(null);
  };

  return (
    <Box>
      <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#0f172a", mb: 1 }}>
        {label}
      </Typography>
      <Box
        onClick={() => setOpen(true)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "#fff",
          border: "2px solid",
          borderColor: error && !selectedEvent ? "#ef4444" : selectedEvent ? theme.primary : "#e5e7eb",
          borderRadius: "12px",
          p: 2,
          cursor: "pointer",
          ...(selectedEvent && { bgcolor: `${theme.primary}08` }),
          "&:hover": { borderColor: selectedEvent ? theme.primary : "#d1d5db" },
        }}
      >
        {selectedEvent ? (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flex: 1, minWidth: 0 }}>
              <CalendarTodayOutlinedIcon sx={{ fontSize: 20, color: theme.primary }} />
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: theme.primary }} noWrap>
                {selectedEvent.title}
              </Typography>
            </Box>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleClear(); }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flex: 1 }}>
              <CalendarTodayOutlinedIcon sx={{ fontSize: 20, color: "#64748b" }} />
              <Typography sx={{ fontSize: 15, color: "#64748b" }}>
                Search and select an event
              </Typography>
            </Box>
            <ExpandMoreIcon sx={{ fontSize: 22, color: "#475569" }} />
          </>
        )}
      </Box>
      {error && !selectedEvent && (
        <Typography sx={{ fontSize: 13, color: "#ef4444", mt: 1, pl: 0.5 }}>{error}</Typography>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1.125rem" }}>Select Event</Typography>
          <IconButton onClick={() => { setOpen(false); setSearchQuery(""); }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search events by title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#64748b" }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#f9fafb" } }}
          />
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress sx={{ color: theme.primary }} />
            </Box>
          ) : filteredEvents.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <CalendarTodayOutlinedIcon sx={{ fontSize: 40, color: "#94a3b8", mb: 1 }} />
              <Typography sx={{ fontSize: 14, color: "#64748b" }}>
                {searchQuery ? "No matching events found" : includePast ? "No events found" : "No upcoming events found"}
              </Typography>
            </Box>
          ) : (
            <List disablePadding sx={{ maxHeight: 360, overflow: "auto" }}>
              {filteredEvents.map((item) => (
                <ListItemButton
                  key={item.id}
                  onClick={() => handleEventSelect(item)}
                  sx={{
                    borderRadius: "8px",
                    mb: 0.5,
                    bgcolor: selectedEvent?.id === item.id ? `${theme.primary}08` : "transparent",
                    "&:hover": { bgcolor: `${theme.primary}08` },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#0f172a", mb: 0.5 }}>
                      {item.title}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                      {item.start_datetime && (
                        <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                          {formatDate(item.start_datetime)}
                        </Typography>
                      )}
                      {item.city && (
                        <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                          {item.is_virtual ? "Virtual" : item.city}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {selectedEvent?.id === item.id && (
                    <CheckCircleIcon sx={{ color: theme.primary, ml: 1 }} />
                  )}
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
